import SignalsContainer from "components/tags/SignalsContainer";
import Property from "lib/core_components/Property";
import { useSupplierStats } from "lib/hooks/api/organisations/useOrganisationStats";
import { SignalCategory } from "lib/StotlesApi";
import { EventData, EventNames, TrackingProvider } from "lib/tracking";
import { EM_DASH } from "lib/utils";
import { SupplierFromQuery } from "../../lib/hooks/api/supplier/useSupplierDetails";
import { SaveListButton } from "../entities/save_components/lists/SaveListButton";
import { PrimaryTab, SecondaryTab } from "./supplier_utils";
import SupplierHeaderCards from "./SupplierHeaderCards";

import css from "./SupplierHeader.module.scss";

import SupplierIcon from "../../../assets/images/icons/supplier.svg";

type Props = {
  handleNavigation: (
    location: PrimaryTab | SecondaryTab,
    eventName: EventNames,
    data: EventData,
  ) => void;
  supplier: SupplierFromQuery;
  /** Current tab path relative to /suppliers/$id; forwarded to header cards for highlighting. */
  currentTabPath: string;
};

export default function SupplierHeader({
  handleNavigation,
  supplier,
  currentTabPath,
}: Props): JSX.Element {
  const { data: organisationStats, isLoading: isLoadingOrganisationStats } = useSupplierStats(
    supplier.guid,
    { includeSignals: true },
  );

  // TODO: Reintroduce when org enrichment on reference rgs is improved with D&B and Companies House data
  // const refOrg = supplier.referenceOrganisations?.find(
  //   (ro) =>
  //     ro.source === SupplierDetailsDtoRefOrgInfoSourceEnum.CompaniesHouse ||
  //     ro.source === SupplierDetailsDtoRefOrgInfoSourceEnum.DunAndBradstreet,
  // );

  return (
    <TrackingProvider data={{ "Context source": "Header" }}>
      <div className={css.header} aria-label="Supplier header">
        <div className={css.inlineTitleActions}>
          <span className={css.title}>
            <img src={SupplierIcon} className={css.placeholderIcon} />
            <h1>{supplier.name}</h1>
          </span>
          <SaveListButton
            entityIds={[supplier.guid]}
            entityType="supplier"
            trackingData={{ "Context source": "Header" }}
            buttonProps={{
              icon: {
                icon: "plus",
              },
              buttonText: "Add to list",
            }}
          />
        </div>
        <div className={css.stats} aria-label="Supplier properties">
          <Property className={css.statistic} title="Country" value={supplier.country} />
          {/* refOrg?.address ? (
            <Property
              className={css.statistic}
              title="Address"
              value={
                <TextLink
                  to={`https://www.google.com/maps/search/?api=1&query=${formatAddress(
                    refOrg.address,
                    supplier?.town,
                  )}`}
                  targetType="external"
                >
                  {formatAddress(refOrg.address, supplier?.town)}
                </TextLink>
              }
            />
          ) : ( */}
          <Property className={css.statistic} title="Region" value={supplier.town} />
          {/* )} */}
          <Property
            className={css.statistic}
            title="SME"
            value={supplier.isSme ? "Yes" : supplier.isSme === false ? "No" : EM_DASH}
          />
          {/* refOrg?.basicContactInfo?.websiteUrl && (
              <Property
                className={css.statistic}
                title="Website"
                value={
                  <TextLink to={refOrg.basicContactInfo.websiteUrl} targetType="external">
                    {refOrg.basicContactInfo.websiteUrl}
                  </TextLink>
                }
              />
            )} */}
          <Property
            title="Signals"
            value={
              organisationStats?.signals ? (
                <SignalsContainer
                  signals={organisationStats.signals}
                  maxSignals={3}
                  requiredDataType="SUPPLIERS"
                  redactedSignalCategories={[SignalCategory.COMPETITOR, SignalCategory.PARTNER]}
                  contextSource="Supplier header"
                />
              ) : null
            }
          />
        </div>
        <SupplierHeaderCards
          handleNavigation={handleNavigation}
          awardsCount={organisationStats?.awardsCount ?? 0}
          mentionsCount={organisationStats?.mentionsCount ?? 0}
          expiriesCount={organisationStats?.expiriesCount ?? 0}
          keyAccountsCount={organisationStats?.keyAccountsCount ?? 0}
          isLoading={isLoadingOrganisationStats}
          currentTabPath={currentTabPath}
        />
      </div>
    </TrackingProvider>
  );
}
